class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    // Skip validation for non-number inputs (like textareas)
    if (event.target.type !== 'number') {
      return;
    }

    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else {
      const stepValue = parseInt(event.target.step, 10);
      if (event.target.step && !isNaN(stepValue) && stepValue > 0 && inputValue % stepValue !== 0) {
        message = window.quickOrderListStrings.step_error.replace('[step]', stepValue);
      }
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    // Prevent automatic DOM recreation - let CartUpdateManager handle updates efficiently
    if (window.cartUpdateManager && window.cartUpdateManager.isUpdating) {
      console.log('onCartUpdate skipped - CartUpdateManager is handling updates');
      return;
    }

    // Only update cart icon and live regions, not full cart content
    this.updateCartIconOnly();
  }

  updateCartIconOnly() {
    // Lightweight update for cart icon bubble only
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cartData) => {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const countElement = cartIconBubble.querySelector('.cart-count-bubble span');
          if (countElement) {
            countElement.textContent = cartData.item_count;
          }
        }
      })
      .catch((error) => console.error('Failed to update cart icon:', error));
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, _name, variantId) {
    this.enableLoading(line);

    // Show simple loading immediately - will be visible when drawer opens
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.showSimpleLoading();
    }

    // Dispatch cart updating event
    document.dispatchEvent(
      new CustomEvent('cart:updating', {
        detail: { line, quantity, variantId },
      })
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // Immediately hide cart footer and price stages when cart becomes empty
        if (parsedState.item_count === 0) {
          const cartDrawerFooter = document.querySelector('#CartDrawer .cart-footer__subtotal-sticky-wrapper');
          const priceStages = document.querySelector('#CartDrawer .cart-drawer-price-stages');

          if (cartDrawerFooter) {
            cartDrawerFooter.style.display = 'none';
          }
          if (priceStages) {
            priceStages.style.display = 'none';
          }
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

          // Handle cart drawer updates directly since CartUpdateManager is disabled
          if (section.id === 'CartDrawer') {
            // Trigger cart drawer update directly
            const cartDrawerItems = document.querySelector('cart-drawer-items');
            if (cartDrawerItems) {
              // For empty-to-non-empty transitions, use immediate update
              const wasEmpty = this.classList.contains('is-empty');
              const willBeEmpty = parsedState.item_count === 0;

              if (wasEmpty && !willBeEmpty) {
                // Empty to non-empty: immediate update
                cartDrawerItems.onCartUpdate();
              } else if (!wasEmpty && willBeEmpty) {
                // Non-empty to empty: force DOM refresh (same as clear cart button)
                // This ensures mini collection resets to show empty cart settings
                cartDrawerItems.onCartUpdate();
              } else {
                // Normal update with slight delay
                setTimeout(() => {
                  cartDrawerItems.onCartUpdate();
                }, 100);
              }
            }
          } else {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        // Focus management removed to prevent unwanted scrolling
        // const lineItem =
        //   document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        // if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
        //   cartDrawerWrapper
        //     ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
        //     : lineItem.querySelector(`[name="${name}"]`).focus();
        // } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
        //   trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        // } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
        //   trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        // }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        // Dispatch cart updated event
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { parsedState, variantId, source: 'cart-items' },
          })
        );
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;

        // Dispatch cart updated event even on error
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { error: true },
          })
        );
      })
      .finally(() => {
        this.disableLoading(line);

        // Update cart icon
        this.updateCartIconOnly();

        // Don't hide loading here - let onCartUpdate() handle it after DOM refresh completes
        // The loading overlay will be hidden by cart-drawer.js after the DOM is fully updated
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    // Use CartLoadingManager for consistent loading states
    if (window.cartLoadingManager) {
      window.cartLoadingManager.startCartItemLoading(line);
    } else {
      // Fallback to original implementation
      this.enableLoadingFallback(line);
    }

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  enableLoadingFallback(line) {
    const cartItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
    if (cartItem) {
      cartItem.classList.add('cart-item--loading');

      if (!cartItem.querySelector('.cart-item__loading-overlay')) {
        const loadingOverlay = document.createElement('div');
        loadingOverlay.className = 'cart-item__loading-overlay';
        loadingOverlay.innerHTML = `
          <div class="cart-item__loading-spinner">
            <svg class="spinner" viewBox="0 0 50 50">
              <circle class="path" cx="25" cy="25" r="20" fill="none" stroke="#dc600e" stroke-width="2" stroke-linecap="round" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
        `;
        cartItem.appendChild(loadingOverlay);
      }
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    const quantityInputs = this.querySelectorAll(
      `#CartItem-${line} input[type="number"], #CartDrawer-Item-${line} input[type="number"]`
    );
    quantityInputs.forEach((input) => {
      input.disabled = true;
      input.style.opacity = '0.5';
      input.style.background = 'linear-gradient(90deg, #f8f8f8, #e8e8e8, #f8f8f8)';
      input.style.backgroundSize = '200% 100%';
      input.style.animation = 'shimmer 1.5s infinite';
    });

    const removeButtons = this.querySelectorAll(
      `#CartItem-${line} cart-remove-button, #CartDrawer-Item-${line} cart-remove-button`
    );
    removeButtons.forEach((button) => {
      button.style.opacity = '0.5';
      button.style.pointerEvents = 'none';
    });
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    // Use CartLoadingManager for consistent loading states
    if (window.cartLoadingManager) {
      window.cartLoadingManager.stopCartItemLoading(line);
    } else {
      // Fallback to original implementation
      this.disableLoadingFallback(line);
    }
  }

  disableLoadingFallback(line) {
    const cartItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
    if (cartItem) {
      cartItem.classList.remove('cart-item--loading');

      const loadingOverlay = cartItem.querySelector('.cart-item__loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.remove();
      }
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));

    const quantityInputs = this.querySelectorAll(
      `#CartItem-${line} input[type="number"], #CartDrawer-Item-${line} input[type="number"]`
    );
    quantityInputs.forEach((input) => {
      input.disabled = false;
      input.style.opacity = '1';
      input.style.background = '';
      input.style.backgroundSize = '';
      input.style.animation = '';
    });

    const removeButtons = this.querySelectorAll(
      `#CartItem-${line} cart-remove-button, #CartDrawer-Item-${line} cart-remove-button`
    );
    removeButtons.forEach((button) => {
      button.style.opacity = '1';
      button.style.pointerEvents = 'auto';
    });
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
